import React from 'react';
import format from 'date-fns/format';
import { timeZoneToUTC } from 'Utils/UTCConverter';
import { DATE_FORMATS } from 'Utils/formats';
import useWebsocket from 'Utils/useWebsocket';
import { shouldBeValue } from 'Features/Reports/reportUtils';
import { TimeseriesContext } from './Timeseries.Context';
import { DIRTY_DATA_TABLE_TYPE, REPORT_DATA_TABLE_TYPE, transformToArray } from './Timeseries.Utils';
import { getWsHost } from '../getWsHostName';

export const useTableDataLoader = (paginate) => {
  const {
    columns, calcModuleParams, pageSize, date, page, useGetDataQuery, tableType, sourceId, zone, sourceType, meteocodeFilter,
  } = React.useContext(TimeseriesContext);
  const {
    isConnected,
    disconnect,
    subscribe,
    processMessage,
  } = useWebsocket({ url: getWsHost(process.env.REACT_APP_WS_TS_PATH), skip: tableType === DIRTY_DATA_TABLE_TYPE });

  const [tableData, setTableData] = React.useState([]);
  const [isShowSpinner, setIsShowSpinner] = React.useState(false);
  const subscriptionPaths = React.useMemo(() => {
    const paths = [];
    if (tableType !== DIRTY_DATA_TABLE_TYPE) {
      if (tableType === REPORT_DATA_TABLE_TYPE) {
        return [...new Set(columns.map((column) => `${process.env.REACT_APP_WS_TS_PATH}/${column.sourceType}/${column.sourceId}`))];
      }
      return [`${process.env.REACT_APP_WS_TS_PATH}/${sourceType}/${sourceId}`];
    }
    return paths;
  }, [columns, sourceId, sourceType, tableType]);

  const [getData, {
    data = {}, isLoading: isLoadingData,
  }] = useGetDataQuery();
  const [realColumns, setRealColumns] = React.useState([])

  const getFetchData = React.useCallback((pullDate) => {
    const fetchParams = {
      startDate: timeZoneToUTC(new Date(paginate.startDate), zone, DATE_FORMATS.api),
      endDate: pullDate
        ? format(new Date(pullDate), DATE_FORMATS.api)
        : timeZoneToUTC(new Date(paginate.endDate), zone, DATE_FORMATS.api),
    };

    // проверка на наличие всех необходимых данных
    if (columns.some((column) => !column.meteocode || !column.sourceId || !column.sourceType)) {
      return null;
    }

    if (tableType !== DIRTY_DATA_TABLE_TYPE) {
      return {
        ...fetchParams,
        ...calcModuleParams,
        columns, // если сломается, то вернуть как было))))
        // columns: columns.map((column) => ({
        //   meteocode: column.meteocode,
        //   sourceId: column.sourceId,
        //   sourceType: column.sourceType,
        //   stationEntityId: column.stationEntityId || (column.stationEntity?.id || -1),
        //   steps: column.steps,
        // })),
      };
    }

    return {
      ...fetchParams,
      datasourceId: sourceId,
      columns: columns.map((column) => ({
        meteocode: column.meteocode,
        stationEntityId: column.stationEntityId || -1,
        place: column.place || '',
      })),
    };
  }, [calcModuleParams, columns, paginate, zone, sourceId, tableType]);

  const pullData = React.useCallback(async (pullDate) => {
    if (columns.length > 0) {
      const fetchData = getFetchData(pullDate);
      if (fetchData) {
        await getData(fetchData).unwrap();
      }
    }
  }, [columns.length, getData, getFetchData]);

  const pullDataOnScroll = () => {
    setIsShowSpinner(false);
    pullData(data?.dateTimePullMore);
  };

  const loadMoreData = (data?.dateTimePullMore ? () => pullDataOnScroll() : () => { });

  React.useEffect(() => () => {
    disconnect();
  }, [disconnect]);

  React.useEffect(() => {
    if (isConnected) {
      subscribe(subscriptionPaths);
    }
  }, [isConnected, subscribe, subscriptionPaths]);

  React.useEffect(() => {
    const tmp = JSON.parse(JSON.stringify(tableData));
    const process = (msg) => {
      const dateTime = format(new Date(msg.dateTime), DATE_FORMATS.timeseries.withSeconds);
      const rowIndex = tmp.findIndex((row) => row.includes(dateTime));
      const columnIndex = columns.findIndex((column) => {
        if (meteocodeFilter === 'entity') {
          return column.meteocode === msg.meteoCode.codeString
          && column.sourceId === msg.sourceId
          && column.stationEntityId === msg.stationEntityId;
        }
        return column.meteocode === msg.meteoCode.codeString
        && column.sourceId === msg.sourceId
        && column.place === msg.place;
      });
      if (rowIndex !== -1) {
        if (columnIndex !== -1) {
          tmp[rowIndex][columnIndex + 1].hasWebsocketData = true;
          const foundedValueIndex = tmp[rowIndex][columnIndex + 1].values
            .findIndex((value) => (value.place === msg.place && value.point === msg.point));
          if (foundedValueIndex !== -1) {
            tmp[rowIndex][columnIndex + 1].values[foundedValueIndex] = msg;
          } else {
            tmp[rowIndex][columnIndex + 1].values.push(msg);
          }
        }
      } else {
        const {
          startDate,
          endDate,
        } = paginate;
        const startDateUTC = timeZoneToUTC(startDate, zone, DATE_FORMATS.timeseries.withSeconds);
        const endDateUTC = timeZoneToUTC(endDate, zone, DATE_FORMATS.timeseries.withSeconds);

        if (new Date(dateTime).getTime() >= new Date(startDateUTC).getTime()
          && new Date(dateTime).getTime() <= new Date(endDateUTC).getTime()
        ) {
          const rowData = [];
          columns.forEach((_, index) => {
            const obj = {
              shouldBeValues: shouldBeValue.EMPTY,
              values: [],
              hasWebsocketData: true,
            };
            if (index === columnIndex) {
              obj.values.push(msg);
            }
            rowData.push(obj);
          });
          const row = [dateTime, ...rowData];
          tmp.push(row);
        }
      }
    };
    subscriptionPaths.forEach((path) => {
      processMessage(path, process, () => {
        const newTableData = tmp.sort((a, b) => new Date(b[0]).getTime() - new Date(a[0]).getTime());
        setTableData(newTableData);
      });
    });
  }, [columns, meteocodeFilter, paginate, processMessage, subscriptionPaths, tableData, zone]);

  React.useEffect(() => {
    setTableData([]);
    setIsShowSpinner(true);
    pullData();
  }, [pullData]);

  React.useEffect(() => {
    if (Object.keys(data).length > 0) {
      const table = transformToArray(data.table || {});
      setTableData((cur) => [...cur, ...table]);
    }
  }, [data]);

  const dependencies = React.useMemo(() => ([
    pageSize,
    page,
    date.startDate,
    date.endDate,
    ...(calcModuleParams ? [calcModuleParams.calc, calcModuleParams.round, calcModuleParams.includeOffGridData] : []),
  ]), [calcModuleParams, date.endDate, date.startDate, page, pageSize]);

  React.useEffect(() => {
    setIsShowSpinner(true);
    setTableData([]);
  }, [dependencies]);

  React.useEffect(() => {
    if (tableData.length === 0 && columns.length > 0
      && !isLoadingData
      && data
      && data.table
      && Object.keys(data.table).length === 0
      && data?.dateTimePullMore
    ) {
      setIsShowSpinner(true);
      pullData(data?.dateTimePullMore);
    }
    setRealColumns(data.realColumns)
  }, [data, isLoadingData, columns.length, pullData, tableData, tableData.length]);

  return {
    tableData,
    isLoadingData,
    loadMoreData,
    isShowSpinner,
    realColumns
  };
};
